import React from "react";
import { Card, CardHeader, CardContent } from "./ui/card";
import { 
  FileText, 
  Search, 
  Leaf, 
  Award,
  CheckCircle,
  Shield,
  Brain,
  TrendingUp
} from "lucide-react";

const ServicesSection = () => {
  const services = [
    {
      icon: <FileText className="h-8 w-8 text-green-600" />,
      title: "Négociation de contrats",
      description: "Nous négocions pour vous les meilleures offres en électricité et en gaz auprès des principaux fournisseurs du marché. Obtenez des tarifs compétitifs et réduisez vos coûts énergétiques.",
      color: "green"
    },
    {
      icon: <Search className="h-8 w-8 text-emerald-600" />,
      title: "Audit énergétique",
      description: "Nous réalisons un diagnostic complet de votre consommation et identifions des leviers d'économies. Détectez les inefficacités et optimisez vos contrats pour une facturation plus juste.",
      color: "emerald"
    },
    {
      icon: <Leaf className="h-8 w-8 text-green-600" />,
      title: "Accompagnement transition énergétique",
      description: "Nous vous orientons vers les meilleures solutions d'énergie verte disponibles sur le marché. Électricité d'origine renouvelable, biogaz, compensations carbone.",
      color: "green"
    },
    {
      icon: <Award className="h-8 w-8 text-emerald-600" />,
      title: "Optimisation des aides publiques",
      description: "Nous vous aidons à bénéficier des dispositifs de financement disponibles (Certificats d'Économies d'Énergie, aides locales, subventions professionnelles).",
      color: "emerald"
    }
  ];

  const advantages = [
    {
      icon: <Shield className="h-6 w-6 text-green-600" />,
      title: "Une équipe d'experts pour vous accompagner à chaque étape"
    },
    {
      icon: <CheckCircle className="h-6 w-6 text-emerald-600" />,
      title: "100% indépendants vis-à-vis des fournisseurs d'énergie"
    },
    {
      icon: <Brain className="h-6 w-6 text-green-600" />,
      title: "Connaissance approfondie du marché de l'énergie"
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-emerald-600" />,
      title: "Économies immédiates et durables"
    }
  ];

  return (
    <section id="services" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Nos Services de Courtage Énergétique
          </h2>
          <p className="text-2xl text-gray-600 max-w-4xl mx-auto">
            Des solutions complètes pour optimiser vos contrats d'énergie et réduire durablement vos coûts
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-10 mb-20">
          {services.map((service, index) => (
            <Card key={index} className="bg-white hover:shadow-2xl transition-all duration-300 border-0 shadow-lg group rounded-2xl">
              <CardHeader className="pb-6 p-10">
                <div className={`inline-flex p-4 rounded-2xl bg-${service.color}-100 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {service.icon}
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
              </CardHeader>
              <CardContent className="px-10 pb-10">
                <p className="text-gray-600 leading-relaxed text-lg">
                  {service.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Advantages Section */}
        <div className="bg-white rounded-3xl p-12 shadow-2xl border-0">
          <h3 className="text-4xl font-bold text-gray-900 text-center mb-16">
            Nos Avantages
          </h3>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {advantages.map((advantage, index) => (
                <div key={index} className="flex items-start space-x-6 p-6 rounded-2xl hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex-shrink-0 mt-1">
                    {advantage.icon}
                  </div>
                  <p className="text-gray-700 font-medium leading-relaxed text-lg">
                    {advantage.title}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1658753145551-8f44e5811d21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxlbmVyZ3klMjBlZmZpY2llbmN5fGVufDB8fHx8MTc1ODAzNjE5OXww&ixlib=rb-4.1.0&q=85"
                alt="Tableau de bord énergétique intelligent"
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-900/20 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-12 px-12 rounded-3xl shadow-2xl">
            <h3 className="text-4xl font-bold mb-6">
              Prêt à optimiser vos contrats d'énergie ?
            </h3>
            <p className="text-2xl text-green-100 mb-8">
              Nos experts analysent votre situation et vous proposent des solutions personnalisées
            </p>
            <button
              onClick={() => document.querySelector("#contact").scrollIntoView({ behavior: "smooth" })}
              className="bg-white text-green-600 hover:bg-gray-100 px-10 py-4 rounded-xl font-semibold text-xl transition-colors duration-200 shadow-lg"
            >
              Demander un audit gratuit
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;